/**
 * Broker / Agent profile page.
 *
 * Route: /broker/:id — pulls Agent + their listings + reviews from
 * /api/agents/:id (the backend endpoint already returns all three in one
 * request). No mock data: empty sections render an empty-state when there's
 * nothing to show.
 *
 * Visual language matches the rest of the site: soft purple gradient header,
 * rounded cards, the existing VerificationBadge component.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { VerificationBadge, type BadgeTier } from '../../components/verification';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import Pagination from '../../components/base/Pagination';
import ResultCard from '../search/components/ResultCard';
import { toFrontendProperty, type SearchProperty } from '../../services/ragSearchService';
import type { Property } from '../../mocks/properties';
import { apiRequest } from '../../utils/apiClient';

interface ApiAgentReview {
  id: string;
  rating: number;
  comment?: string | null;
  authorName?: string | null;
  createdAt: string;
}

interface ApiAgentProperty {
  id: string;
  title: string;
  titleAr?: string | null;
  slug?: string | null;
  price: number;
  bedrooms?: number | null;
  bathrooms?: number | null;
  sqft?: number | null;
  address?: string | null;
  city?: { nameEn?: string | null; nameAr?: string | null } | null;
  images?: { url: string; isPrimary?: boolean }[] | null;
}

interface ApiAgentDetail {
  id: string;
  displayName?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  whatsapp?: string | null;
  companyId?: string | null;
  isVerified: boolean;
  falVerificationStatus?: string | null;
  currentBadgeType?: string | null;
  avgRating?: number | null;
  totalReviews?: number | null;
  totalSold?: number | null;
  totalRented?: number | null;
  totalListings?: number | null;
  region?: string | null;
  regionAr?: string | null;
  districtName?: string | null;
  districtNameAr?: string | null;
  createdAt?: string | null;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    avatarUrl?: string | null;
    email?: string | null;
  } | null;
  company?: {
    id?: string;
    name?: string | null;
    nameAr?: string | null;
    slug?: string | null;
    logoUrl?: string | null;
  } | null;
  properties?: ApiAgentProperty[];
  reviews?: ApiAgentReview[];
}

interface AgentResponse {
  data?: ApiAgentDetail;
}

function badgeTierFromAgent(agent: ApiAgentDetail): BadgeTier {
  const t = (agent.currentBadgeType || '').toUpperCase();
  if (t.includes('GOLD')) return 'gold';
  if (t.includes('COMPANY')) return 'company';
  return 'verified';
}

function joinedYear(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return String(d.getFullYear());
}

export default function BrokerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [agent, setAgent] = useState<ApiAgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listings are fetched separately (paginated, full find-homes shape) so the
  // profile can render the identical PropertyCard with image carousel.
  const [listings, setListings] = useState<Property[]>([]);
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsTotalPages, setListingsTotalPages] = useState(1);
  const [listingsLoading, setListingsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiRequest<AgentResponse>(`/agents/${id}`)
      .then((res) => {
        if (cancelled) return;
        if (!res.data) {
          setError(isRTL ? 'لم يتم العثور على هذا الوسيط' : 'Broker not found');
        } else {
          setAgent(res.data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load broker';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isRTL]);

  // Paginated listings in the full find-homes shape.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setListingsLoading(true);
    apiRequest<{ data: SearchProperty[]; meta?: { totalPages?: number } }>(
      `/agents/${id}/properties?page=${listingsPage}&pageSize=12`
    )
      .then((res) => {
        if (cancelled) return;
        setListings((res.data ?? []).map(toFrontendProperty) as Property[]);
        setListingsTotalPages(res.meta?.totalPages ?? 1);
      })
      .catch(() => {
        if (cancelled) return;
        setListings([]);
        setListingsTotalPages(1);
      })
      .finally(() => {
        if (!cancelled) setListingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, listingsPage]);

  const derived = useMemo(() => {
    if (!agent) return null;
    const userFullName = [agent.user?.firstName, agent.user?.lastName].filter(Boolean).join(' ').trim();
    const name = agent.displayName || userFullName || (isRTL ? 'وسيط' : 'Broker');
    const photo = agent.avatarUrl || agent.user?.avatarUrl || null;
    const tier = badgeTierFromAgent(agent);
    const rating = typeof agent.avgRating === 'number' ? agent.avgRating : 0;
    const reviewCount = agent.totalReviews ?? agent.reviews?.length ?? 0;
    const transactions = (agent.totalSold ?? 0) + (agent.totalRented ?? 0);
    const listingsCount = agent.totalListings ?? agent.properties?.length ?? 0;
    const sold = agent.totalSold ?? 0;
    const rented = agent.totalRented ?? 0;
    const city = isRTL ? (agent.regionAr || agent.region) : agent.region;
    const district = isRTL ? (agent.districtNameAr || agent.districtName) : agent.districtName;
    const joined = joinedYear(agent.createdAt);
    const phoneDigits = (agent.businessPhone || agent.whatsapp || '').replace(/[^0-9]/g, '');
    return {
      name,
      photo,
      tier,
      rating,
      reviewCount,
      transactions,
      listingsCount,
      sold,
      rented,
      city,
      district,
      joined,
      phoneDigits,
      bio: agent.bio || '',
      whatsapp: agent.whatsapp || agent.businessPhone || '',
      phone: agent.businessPhone || agent.whatsapp || '',
      email: agent.businessEmail || agent.user?.email || '',
      company: agent.company || null,
    };
  }, [agent, isRTL]);

  const renderEmpty = (icon: string, title: string, description?: string) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
        <i className={`${icon} text-2xl text-[#004D40]`} />
      </div>
      <h4 className="text-base font-semibold text-slate-900 mb-1">{title}</h4>
      {description && <p className="text-sm text-slate-500">{description}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#003930] mb-5 transition-colors"
        >
          <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line`} />
          {isRTL ? 'رجوع' : 'Back'}
        </button>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-red-50 rounded-full flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-1">
              {isRTL ? 'تعذّر تحميل بيانات الوسيط' : 'Could not load broker'}
            </h2>
            <p className="text-sm text-slate-500 mb-4">{error}</p>
            <Link
              to="/trusted-brokers"
              className="inline-flex items-center gap-2 px-5 py-2 bg-[var(--pv-color-surface-sunken)] text-[#003930] rounded-full text-sm font-medium hover:bg-[#DDEEEB] transition-colors"
            >
              {isRTL ? 'العودة إلى قائمة الوسطاء' : 'Back to brokers'}
            </Link>
          </div>
        )}

        {!loading && !error && agent && derived && (
          <>
            {/* Profile header */}
            <section
              className="relative overflow-hidden rounded-3xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6"
            >
              <div className="h-24 bg-[var(--pv-color-surface-sunken)] border-b border-[var(--pv-border-soft)]" />
              <div className="px-6 sm:px-8 pb-7 -mt-12 sm:-mt-14">
                <div className="flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-7">
                  {/* Avatar */}
                  <div className="relative">
                    {derived.photo ? (
                      <img
                        src={derived.photo}
                        alt={derived.name}
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover ring-4 ring-white shadow-md bg-white"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-md bg-[#004D40] flex items-center justify-center ${derived.photo ? 'hidden' : ''}`}>
                      <span className="text-white text-3xl font-bold">{derived.name.charAt(0).toUpperCase()}</span>
                    </div>
                  </div>

                  {/* Name + meta */}
                  <div className="flex-1 min-w-0 pt-2 sm:pt-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl sm:text-2xl font-bold text-[var(--pv-color-primary)] truncate">
                        {derived.name}
                      </h1>
                      <VerificationBadge tier={derived.tier} size="sm" showLabel={false} showTooltip={false} />
                    </div>
                    {derived.company?.name && (
                      <p className="text-sm text-slate-500 mt-0.5">
                        {isRTL ? (derived.company.nameAr || derived.company.name) : derived.company.name}
                      </p>
                    )}
                    <div className="flex items-center flex-wrap gap-3 mt-2.5 text-xs text-slate-500">
                      {derived.rating > 0 && (
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400" />
                          <span className="font-semibold text-slate-700">{derived.rating.toFixed(1)}</span>
                          {derived.reviewCount > 0 && <span>({derived.reviewCount})</span>}
                        </span>
                      )}
                      {derived.city && (
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-2-line" />
                          {derived.city}{derived.district ? `, ${derived.district}` : ''}
                        </span>
                      )}
                      {derived.joined && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {isRTL ? `منضم منذ ${derived.joined}` : `Joined ${derived.joined}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contact actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {derived.phoneDigits && (
                      <a
                        href={`https://wa.me/${derived.phoneDigits}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#004D40] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
                        title={isRTL ? 'تواصل عبر واتساب' : 'WhatsApp'}
                      >
                        <i className="ri-whatsapp-line" />
                        <span className="hidden sm:inline">{isRTL ? 'واتساب' : 'WhatsApp'}</span>
                      </a>
                    )}
                    {derived.phone && (
                      <a
                        href={`tel:${derived.phone}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
                        title={isRTL ? 'اتصل' : 'Call'}
                      >
                        <i className="ri-phone-line" />
                        <span className="hidden sm:inline">{isRTL ? 'اتصال' : 'Call'}</span>
                      </a>
                    )}
                    {derived.email && (
                      <a
                        href={`mailto:${derived.email}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
                        title={isRTL ? 'بريد إلكتروني' : 'Email'}
                      >
                        <i className="ri-mail-line" />
                        <span className="hidden sm:inline">{isRTL ? 'إيميل' : 'Email'}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: isRTL ? 'إعلانات' : 'Listings', value: derived.listingsCount, icon: 'ri-building-line' },
                { label: isRTL ? 'مبيعات' : 'Sold', value: derived.sold, icon: 'ri-price-tag-3-line' },
                { label: isRTL ? 'إيجارات' : 'Rented', value: derived.rented, icon: 'ri-key-line' },
                { label: isRTL ? 'التقييم' : 'Rating', value: derived.rating > 0 ? derived.rating.toFixed(1) : '-', icon: 'ri-star-line' },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl bg-white border border-slate-200/80 p-4">
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                    <i className={s.icon} />
                    {s.label}
                  </div>
                  <div className="text-xl font-semibold text-slate-900">{s.value}</div>
                </div>
              ))}
            </section>

            {/* About */}
            {derived.bio && (
              <section className="rounded-2xl bg-white border border-slate-200/80 p-6 mb-6">
                <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-2">
                  {isRTL ? 'نبذة' : 'About'}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{derived.bio}</p>
              </section>
            )}

            {/* Listings */}
            <section className="rounded-2xl bg-white border border-slate-200/80 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-[var(--pv-color-primary)]">
                  {isRTL ? 'الإعلانات' : 'Listings'}
                  {derived.listingsCount > 0 && (
                    <span className="ms-2 text-sm text-slate-400 font-normal">({derived.listingsCount})</span>
                  )}
                </h2>
              </div>
              {listingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
                </div>
              ) : listings.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {listings.map((p, index) => (
                      <ResultCard key={p.id} property={p} eagerImage={index < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
                    ))}
                  </div>
                  <Pagination
                    currentPage={listingsPage}
                    totalPages={listingsTotalPages}
                    onPageChange={setListingsPage}
                    className="mt-8"
                  />
                </>
              ) : (
                renderEmpty(
                  'ri-building-line',
                  isRTL ? 'لا توجد إعلانات نشطة' : 'No active listings',
                  isRTL ? 'سيظهر هنا أي عقار يضيفه الوسيط ويكون نشطاً' : "Active listings will appear here as the broker adds them"
                )
              )}
            </section>

            {/* Reviews */}
            <section className="rounded-2xl bg-white border border-slate-200/80 p-6">
              <h2 className="text-base font-semibold text-[var(--pv-color-primary)] mb-4">
                {isRTL ? 'التقييمات' : 'Reviews'}
                {derived.reviewCount > 0 && (
                  <span className="ms-2 text-sm text-slate-400 font-normal">({derived.reviewCount})</span>
                )}
              </h2>
              {agent.reviews && agent.reviews.length > 0 ? (
                <div className="space-y-4">
                  {agent.reviews.map((r) => (
                    <div key={r.id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-slate-900">
                          {r.authorName || (isRTL ? 'مستخدم' : 'User')}
                        </span>
                        <span className="flex items-center gap-0.5 text-amber-400 text-xs">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i key={i} className={i < Math.round(r.rating) ? 'ri-star-fill' : 'ri-star-line'} />
                          ))}
                        </span>
                      </div>
                      {r.comment && <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                renderEmpty(
                  'ri-chat-quote-line',
                  isRTL ? 'لا توجد تقييمات بعد' : 'No reviews yet',
                  isRTL ? 'كن أول من يقيّم هذا الوسيط بعد التعامل معه' : 'Be the first to leave a review after working with this broker'
                )
              )}
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
